// src/pages/sessions/components/LiveSession/managers/MediaManager.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { Track, DataPacket_Kind, RoomEvent } from 'livekit-client';
import { toast } from 'sonner';
import { useGoogleDocManager } from './GoogleDocManager';
import { isMacOS, isSafari, getDeviceType } from '@/utils/platform';

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function useMediaManager({
  onCloseWhiteboard,
  roomRef,
  sessionId,
  isTeacherUser,
  localParticipantIdentity,
  user,
  sessionSettings
}) {
  // Screen share state
  const [screenSharers, setScreenSharers] = useState([]);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const screenShareTracksRef = useRef(null);
  const screenSharerNamesRef = useRef({});
  
  // Layout mode
  const [layoutMode, setLayoutMode] = useState('grid');
  
  // Mac screen share guide state
  const [showMacScreenGuide, setShowMacScreenGuide] = useState(false);
  
  const mountedRef = useRef(true);
  const cleanupSubscriptionsRef = useRef([]);

  // Google Doc Manager
  const googleDocManager = useGoogleDocManager({
    roomRef,
    user,
    screenSharers,
    setLayoutMode
  });

  // Clean up screen share properly
  const handleScreenShareCleanup = useCallback(async () => {
    if (!roomRef.current || !screenShareTracksRef.current) return;
    
    const room = roomRef.current;
    for (const track of screenShareTracksRef.current) {
      try {
        await room.localParticipant.unpublishTrack(track);
        track.stop();
        track.detach?.();
        // Remove event listeners
        track.removeAllListeners('ended');
      } catch (e) {
        console.error('Error stopping track:', e);
      }
    }
    screenShareTracksRef.current = null;
    
    const announce = { 
      type: 'screen_share_announce', 
      action: 'stopped',
      identity: room.localParticipant?.identity, 
      ts: Date.now() 
    };
    const enc = encoder.encode(JSON.stringify(announce));
    room.localParticipant?.publishData(enc, DataPacket_Kind.RELIABLE).catch(() => {});
    
    if (!googleDocManager.embeddedDocument) {
      setLayoutMode('grid');
    }
    
    toast.info('Screen sharing ended');
  }, [googleDocManager.embeddedDocument]);

  // Start screen share (extracted logic)
  const startScreenShare = useCallback(async () => {
    if (onCloseWhiteboard) onCloseWhiteboard();
    if (!roomRef.current || !mountedRef.current) return;
    
    const room = roomRef.current;
    
    try {
      const { createLocalScreenTracks, Track: TrackClass } = await import('livekit-client');
      const tracks = await createLocalScreenTracks({ 
        audio: true,
        video: true,
        audioOptions: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        }
      });
      
      const videoTrack = tracks.find(t => t.kind === 'video');
      if (!videoTrack) {
        throw new Error('Failed to capture screen video');
      }
      
      videoTrack.source = TrackClass.Source.ScreenShare;
      screenShareTracksRef.current = tracks;
      
      await room.localParticipant.publishTrack(videoTrack, {
        name: 'screen',
        simulcast: false,
        videoCodec: 'vp8',
        encoding: { maxBitrate: 1_500_000, maxFramerate: 15 },
        source: TrackClass.Source.ScreenShare,
      });
      
      const sysAudio = tracks.find(t => t.kind === 'audio');
      if (sysAudio) {
        sysAudio.source = TrackClass.Source.ScreenShareAudio;
        await room.localParticipant.publishTrack(sysAudio, {
          name: 'screen-audio',
          source: TrackClass.Source.ScreenShareAudio,
        });
      }
      
      const ourName = user?.displayName || user?.email || 'Someone';
      const announce = {
        type: 'screen_share_announce',
        action: 'started',
        identity: room.localParticipant?.identity,
        displayName: ourName,
        ts: Date.now(),
      };
      const enc = encoder.encode(JSON.stringify(announce));
      await room.localParticipant.publishData(enc, DataPacket_Kind.RELIABLE);
      
      screenSharerNamesRef.current[room.localParticipant?.identity] = ourName;
      
      // CRITICAL FIX: Properly handle ended event
      const handleEnded = () => {
        handleScreenShareCleanup();
      };
      
      if (videoTrack.once) {
        videoTrack.once('ended', handleEnded);
      } else {
        videoTrack.on('ended', handleEnded);
      }
      
      setLayoutMode('presentation');
      toast.success(sysAudio ? 'Screen sharing started with system audio' : 'Screen sharing started');
    } catch (error) {
      console.error('Screen share error:', error);
      screenShareTracksRef.current = null;
      
      if (error.name === 'NotAllowedError') {
        toast.error('Screen sharing permission denied. Please allow screen sharing in your browser.');
      } else {
        toast.error('Failed to toggle screen sharing');
      }
    }
  }, [handleScreenShareCleanup, user]);

  // Toggle screen share - show Mac guide if needed
  const toggleScreenShare = useCallback(async () => {
    if (!roomRef.current || !mountedRef.current) return;
    
    // If stopping, proceed directly
    if (screenShareTracksRef.current) {
      await handleScreenShareCleanup();
      toast.success('Screen sharing stopped');
      return;
    }
    
    // Show the Mac screen share guide only on desktop macOS Safari —
    // not on phones/tablets (can't screen share), not on Chrome/Edge on Mac
    const deviceType = getDeviceType();
    if (isMacOS() && isSafari() && deviceType === 'desktop') {
      setShowMacScreenGuide(true);
      return;
    }
    
    // Otherwise start directly
    await startScreenShare();
  }, [handleScreenShareCleanup, startScreenShare]);

  // Called when user clicks Continue in the Mac screen share guide
  const proceedWithScreenShare = useCallback(async () => {
    setShowMacScreenGuide(false);
    await startScreenShare();
  }, [startScreenShare]);

  // FIX: Add cleanup effect for screen share tracks
  useEffect(() => {
    return () => {
      if (screenShareTracksRef.current) {
        screenShareTracksRef.current.forEach(track => {
          track.removeAllListeners('ended');
          track.stop();
        });
      }
    };
  }, []);

  // Detect screen shares
  useEffect(() => {
    if (!mountedRef.current || !roomRef.current || !localParticipantIdentity) return;
    
    const room = roomRef.current;
    const screenSharesMap = new Map();

    const updateLayoutAndSharers = () => {
      if (!mountedRef.current) return;
      
      const sharersArray = Array.from(screenSharesMap.values());
      setScreenSharers(sharersArray);
      
      const hasScreenShare = sharersArray.length > 0;
      const isDocActive = !!googleDocManager.embeddedDocument;

      if (hasScreenShare || isDocActive) {
        setLayoutMode('presentation');
      } else {
        setLayoutMode('grid');
      }
    };

    const processTrack = (publication, participant, isAdding) => {
      if (publication.source !== Track.Source.ScreenShare) return;
      if (publication.trackName === 'whiteboard') return;
      const { identity, name, metadata } = participant;
      
      if (isAdding) {
        let displayName = name;
        if (metadata) {
          try {
            const meta = JSON.parse(metadata);
            displayName = meta.displayName || name;
          } catch {}
        }
        screenSharerNamesRef.current[identity] = displayName;
        screenSharesMap.set(identity, {
          identity,
          name: displayName,
          trackSid: publication.trackSid,
          isLocal: identity === localParticipantIdentity,
        });
      } else {
        screenSharesMap.delete(identity);
      }
      updateLayoutAndSharers();
    };

    const handleTrackPublished = (pub, p) => processTrack(pub, p, true);
    const handleTrackUnpublished = (pub, p) => processTrack(pub, p, false);
    const handleLocalTrackPublished = (pub) => processTrack(pub, room.localParticipant, true);
    const handleLocalTrackUnpublished = (pub) => processTrack(pub, room.localParticipant, false);
    
    room.on(RoomEvent.TrackPublished, handleTrackPublished);
    room.on(RoomEvent.TrackUnpublished, handleTrackUnpublished);
    room.on(RoomEvent.LocalTrackPublished, handleLocalTrackPublished);
    room.on(RoomEvent.LocalTrackUnpublished, handleLocalTrackUnpublished);

    // Initial scan
    [room.localParticipant, ...room.remoteParticipants.values()].forEach(p => {
      if (p) {
        p.getTrackPublications().forEach(pub => {
          if (pub.source === Track.Source.ScreenShare) {
            processTrack(pub, p, true);
          }
        });
      }
    });

    // Cleanup
    const cleanup = () => {
      room.off(RoomEvent.TrackPublished, handleTrackPublished);
      room.off(RoomEvent.TrackUnpublished, handleTrackUnpublished);
      room.off(RoomEvent.LocalTrackPublished, handleLocalTrackPublished);
      room.off(RoomEvent.LocalTrackUnpublished, handleLocalTrackUnpublished);
    };

    cleanupSubscriptionsRef.current.push(cleanup);
    return cleanup;
  }, [localParticipantIdentity, googleDocManager.embeddedDocument]);

  // Update isScreenSharing state
  useEffect(() => {
    const amISharing = screenSharers.some(s => 
      s.isLocal || s.identity === localParticipantIdentity
    );
    setIsScreenSharing(amISharing);
  }, [screenSharers, localParticipantIdentity]);

  // Cleanup
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      
      // Clean up subscriptions
      cleanupSubscriptionsRef.current.forEach(cleanup => {
        try {
          cleanup();
        } catch (e) {
          console.debug('Cleanup error:', e);
        }
      });
      cleanupSubscriptionsRef.current = [];
      
      // Clean up screen share tracks
      if (screenShareTracksRef.current) {
        screenShareTracksRef.current.forEach(track => {
          try {
            track.removeAllListeners('ended');
            track.stop();
            track.detach?.();
          } catch {}
        });
        screenShareTracksRef.current = null;
      }
    };
  }, []);

  return {
    // Screen share
    screenSharers,
    isScreenSharing,
    toggleScreenShare,
    screenSharerNamesRef,
    
    // Mac screen share guide
    showMacScreenGuide,
    setShowMacScreenGuide,
    proceedWithScreenShare,
    
    // Document (from GoogleDocManager)
    embeddedDocument: googleDocManager.embeddedDocument,
    documentSharer: googleDocManager.documentSharer,
    toggleDocumentShare: googleDocManager.toggleDocumentShare,
    GoogleDocModalComponent: googleDocManager.GoogleDocModalComponent,
    
    // Layout
    layoutMode,
    setLayoutMode,
  };
}